// ─── Autotile helpers ─────────────────────────────────────────────────────────
// Deterministic grass variation + edge/corner sprite-key selection for path and
// water tiles. All key strings are precomputed in lookup tables so the
// per-frame hot path does zero string building and zero allocation.

import { TileType, type RoomData } from "./constants";

// ── Cheap deterministic 2-D hash (integer math only, no allocation) ──────────
export function hash2D(x: number, y: number): number {
  let h = (x * 374761393 + y * 668265263) | 0;
  h = ((h ^ (h >>> 13)) * 1274126177) | 0;
  return (h ^ (h >>> 16)) >>> 0;
}

// ── Grass variants ────────────────────────────────────────────────────────────
const GRASS_VARIANT_KEYS = [
  "tile_grass_v0",
  "tile_grass_v1",
  "tile_grass_v2",
  "tile_grass_v3",
] as const;

const GRASS_FLOWER_KEYS = ["tile_grass_flower_0", "tile_grass_flower_1"] as const;

/** Plain grass variant for tile (col,row) — always returns one of v0..v3. */
export function grassVariantKey(col: number, row: number): string {
  return GRASS_VARIANT_KEYS[(hash2D(col, row) >>> 4) & 3];
}

/** Flower accent key for ~4% of grass tiles, null for the rest. */
export function grassFlowerKey(col: number, row: number): string | null {
  const h = hash2D(col, row);
  return h % 100 < 4 ? GRASS_FLOWER_KEYS[(h >>> 8) & 1] : null;
}

// ── Edge masks ────────────────────────────────────────────────────────────────
// Bit set when the neighbour on that side matches the "outside" predicate
// (grass for path tiles, land for water tiles).
export const EDGE_N = 1;
export const EDGE_E = 2;
export const EDGE_S = 4;
export const EDGE_W = 8;

function buildEdgeKeyTable(prefix: string): ReadonlyArray<string | null> {
  const t: Array<string | null> = new Array(16).fill(null);
  t[EDGE_N] = `${prefix}_edge_n`;
  t[EDGE_E] = `${prefix}_edge_e`;
  t[EDGE_S] = `${prefix}_edge_s`;
  t[EDGE_W] = `${prefix}_edge_w`;
  t[EDGE_N | EDGE_E] = `${prefix}_corner_ne`;
  t[EDGE_N | EDGE_W] = `${prefix}_corner_nw`;
  t[EDGE_S | EDGE_E] = `${prefix}_corner_se`;
  t[EDGE_S | EDGE_W] = `${prefix}_corner_sw`;
  return t;
}

/** mask → manifest key (null where no single sprite covers the combo). */
export const PATH_EDGE_KEYS = buildEdgeKeyTable("tile_path");
export const WATER_EDGE_KEYS = buildEdgeKeyTable("tile_water");

// ── Neighbour predicates ──────────────────────────────────────────────────────
export function isGrassLike(t: TileType): boolean {
  return t === TileType.Grass || t === TileType.DarkGrass || t === TileType.FlowerPatch;
}

export function isWater(t: TileType): boolean {
  return t === TileType.Ocean || t === TileType.WaveAnimated;
}

/** Land = anything that's not water and not Empty (map edge reads as open sea). */
export function isLand(t: TileType): boolean {
  return !isWater(t) && t !== TileType.Empty;
}

/**
 * 4-neighbour mask for tile (col,row). Out-of-bounds neighbours never match —
 * a path running off the map edge stays plain, ocean keeps flowing.
 */
export function neighborMask(
  room: RoomData,
  col: number,
  row: number,
  predicate: (t: TileType) => boolean,
): number {
  const map = room.tileMap;
  let mask = 0;
  if (row > 0 && predicate(map[row - 1][col] as TileType)) mask |= EDGE_N;
  if (col < room.width - 1 && predicate(map[row][col + 1] as TileType)) mask |= EDGE_E;
  if (row < room.height - 1 && predicate(map[row + 1][col] as TileType)) mask |= EDGE_S;
  if (col > 0 && predicate(map[row][col - 1] as TileType)) mask |= EDGE_W;
  return mask;
}
